/**
 * Contained photography.
 *
 * A ground is scenery: it carries no information, makes no claim, and is
 * never referred to by the copy. What changed is that it is now the thing
 * that puts colour on a marketing page, so it has to be seen. It is
 * contained inside the grid rather than run full width, anchored to one
 * container edge and running off the opposite side of the viewport.
 *
 * A running panel needs `ds-bleedclip` on an ancestor that actually reaches
 * the viewport edge. `DsRoot` applies it once, at the `.ds` scope root, so no
 * page adds it itself. It must never sit on `.ds-container`: see the note in
 * ground.css, `overflow` clips at the padding box, so putting it there clips
 * the running panel straight back to the container's own edge, silently.
 */
import { Children, type ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { useArrived } from './media'
import './ground.css'
// Applies `ds-onground`, defined here, to `.ds-ground__on`. Without this
// import the class works only by accident, if some other module that does
// import media.css happens to already be in the bundle: on its own this
// component would render its copy in `--ds-ink` on a near-black panel.
import './media.css'

/* The four corners a sheet of glass can be offset towards. */
type GlassCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

export function Ground({
  photo,
  runs,
  height,
  children,
  over,
  className,
  eager,
  deep,
}: {
  /** Path to the ground image. */
  photo: string
  /**
   * Which side the panel runs off. It stays anchored to the other.
   *
   * Correct only when `Ground` is a direct child of a viewport-centred
   * container: `calc(50% - 50vw)` resolves its percentage against the
   * immediate parent's content box. Nested inside a grid cell or a card, it
   * lands at an arbitrary offset instead, with no error to catch it.
   */
  runs?: 'left' | 'right'
  /** Minimum height in px. Omit to let the content set it. */
  height?: number
  /** Copy sitting on the photograph. Padded, and it brings the scrim with it. */
  children?: ReactNode
  /**
   * Something positioned against the panel itself rather than flowed inside
   * the padded copy block. Anything meant to cross the panel's edge belongs
   * here: `children` sits inside 64px of padding and cannot reach the edge.
   */
  over?: ReactNode
  className?: string
  /**
   * True for a ground that will be the largest above-the-fold element on the
   * page (the hero). Browsers cannot know that from markup alone, and the
   * default has to stay lazy for every other caller, which is every ground
   * below the fold.
   */
  eager?: boolean
  /**
   * A stronger scrim, for a photograph too bright to carry type under the
   * ordinary one.
   *
   * Measured across a hero's own text block, nine of the eleven grounds in
   * the set could not hold white type at 4.5:1 under the default scrim. A
   * deeper one takes the usable set from two pictures to six. It is opt-in
   * and not the default, because darkening a picture further is a cost paid
   * in the photograph, and it should only be paid where words are actually
   * sitting on it.
   */
  deep?: boolean
}) {
  const hasChildren = Children.count(children) > 0
  /*
    The photograph settles once, the first time the panel is on screen. It is
    the same arrival every measurement in this system uses, by the same hook,
    and it is the only motion a ground gets: no drift on scroll, no zoom that
    never stops. A picture that keeps moving is asking to be watched, and
    scenery is the one thing on a page that is not asking for anything.
  */
  const [ref, arrived] = useArrived<HTMLDivElement>()
  return (
    <div
      ref={ref}
      data-settles="true"
      data-arrived={arrived ? 'true' : undefined}
      className={cn(
        'ds-ground',
        runs === 'right' && 'ds-ground--runs-right',
        runs === 'left' && 'ds-ground--runs-left',
        deep && 'ds-ground--deep',
        className,
      )}
      style={height ? { minHeight: height } : undefined}
    >
      {/*
        The photograph needs a box that clips, because it settles by scaling
        and a scaled picture would otherwise square off the panel's rounded
        corners on the way. The panel itself still cannot clip: `over` exists
        so a card can cross its edge, and clipping the panel would slice that
        card off flat.
      */}
      <span className="ds-ground__frame" aria-hidden="true">
        <img
          className="ds-ground__bg"
          src={photo}
          alt=""
          aria-hidden="true"
          loading={eager ? 'eager' : 'lazy'}
          // Lowercase, not `fetchPriority`: React 18 does not recognise the
          // camelCase prop and drops it with a console warning instead of
          // rendering the attribute.
          {...(eager ? { fetchpriority: 'high' } : {})}
          decoding="async"
        />
      </span>
      {/* The scrim exists to make type readable, so a panel with no type on it
          does not get one. Darkening a picture nobody is reading over is how
          the old grounds became slabs. `Children.count` rather than a truthy
          check: an empty array from `.map()` is still truthy, and would have
          scrimmed a picture with nothing rendered on it. */}
      {hasChildren && (
        <>
          <div className="ds-ground__scrim" aria-hidden="true" />
          <div className="ds-ground__on ds-onground">{children}</div>
        </>
      )}
      {over}
    </div>
  )
}

/**
 * Words on paper, photograph beside them.
 *
 * The composition every hero on this site uses now, and the reason is the
 * imagery. The swirl set is bright, high-key glass, and measured column by
 * column against a hero's own text block not one of the thirteen clears
 * 4.5:1 for white type: the best reaches 3.3 under the deeper scrim and
 * most sit near 2. Type on those pictures is either unreadable or the
 * picture is scrimmed until there is no picture left.
 *
 * So the words come off the photograph. They sit on paper at a proper
 * measure, the picture takes the other half at full strength and runs off
 * the edge of the viewport, and no scrim is needed anywhere because nothing
 * is being read over anything. It is also the composition that lets the
 * pictures be as strong as they are.
 *
 * Correct only as a direct child of a viewport-centred `.ds-container`, the
 * same precondition `runs` and `GroundOffset` carry: `right: calc(50% -
 * 50vw)` resolves its percentage against the immediate parent's box.
 */
export function Beside({
  photo,
  children,
  over,
  glass = { from: 'bottom-right', second: 'top-left' },
  flip,
  height,
  eager,
  className,
}: {
  photo: string
  /** The words, which stay on paper. */
  children: ReactNode
  /**
   * An object laid on the picture: a card, a mockup, a demonstration.
   *
   * Not copy. Words belonging to the page go in `children`, on paper, which
   * is the whole point of this component. This slot is for something that
   * brings its own surface and therefore its own contrast. Below the split
   * it stops being an overlay and stacks after the picture, so whatever goes
   * here has to read on paper as well as on a photograph, which in practice
   * means opaque.
   *
   * It is laid on glass, automatically. Anything set on a photograph in
   * this system gets the two offset sheets behind it, so a page cannot end
   * up with the object sitting flat on the picture by forgetting to ask.
   */
  over?: ReactNode
  /**
   * Which corners the two sheets of glass are offset towards.
   *
   * Vary them per page so two heroes do not stack the same way. Both are
   * corners rather than a boolean: pointing the second sheet at the first
   * one's corner produces a thicker version of it rather than a second
   * sheet.
   */
  glass?: { from: GlassCorner; second: GlassCorner }
  /** Picture on the left instead of the right. */
  flip?: boolean
  /** Minimum height in px for the row. */
  height?: number
  /** True for the hero, which is the largest thing above the fold. */
  eager?: boolean
  className?: string
}) {
  return (
    <div
      className={cn('ds-beside', flip && 'ds-beside--flip', className)}
      style={height ? { minHeight: height } : undefined}
    >
      <div className="ds-beside__words">{children}</div>
      {/*
        The object lives inside the panel, not beside it.

        It used to be a sibling positioned against the row, which worked at
        desktop widths and fell apart below the split: the panel became a
        band and the object stacked underneath it, on paper, with its glass
        switched off and its animation running while it was still off
        screen. Inside the panel it is positioned against the picture in
        both layouts, which is the thing that was actually wanted.
      */}
      <div className="ds-beside__panel">
        {/* No children on the Ground, so it draws no scrim: there is nothing
            on the picture to hold up. */}
        <Ground photo={photo} className="ds-beside__pic" eager={eager} />
        {over && (
          <div className="ds-beside__over">
            <Glass from={glass.from} second={glass.second}>
              {over}
            </Glass>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Words on paper, photograph running off the right, card crossing onto it.
 *
 * The card must cross onto the picture rather than off it onto paper. A
 * white card overhanging white paper keeps only its shadow, which is the
 * reason the other overlap direction is not in this system.
 *
 * Correct only when `GroundOffset` is a direct child of a viewport-centred
 * `.ds-container`, the same precondition `runs` carries on `Ground`: the
 * panel's `right: calc(50% - 50vw)` resolves its percentage against the
 * immediate parent's box. Nested inside a grid cell or a card, it lands at
 * an arbitrary offset instead, with no error to catch it.
 */
export function GroundOffset({
  photo,
  words,
  children,
  className,
}: {
  photo: string
  /** The block that stays on paper. */
  words: ReactNode
  /** The card that crosses onto the photograph. */
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('ds-goffset', className)}>
      <div className="ds-goffset__panel">
        {/* No children, so no scrim: the words are on paper and nothing needs
            to be read over the picture. */}
        <Ground photo={photo} className="ds-goffset__pic" />
      </div>
      <div className="ds-goffset__words">{words}</div>
      <div className="ds-goffset__over">{children}</div>
    </div>
  )
}

/**
 * A slab of glass behind something, offset, so the thing on it floats.
 *
 * Made for an object laid on a photograph: the pane blurs and lifts the
 * picture behind it, and because it is offset it reads as a second surface
 * rather than as a border. Wrap whatever is being laid down and it draws
 * itself behind, with the content above it.
 *
 * The pane's own colours are fixed rather than tokens, and that is the same
 * rule the rest of this system follows for anything sitting on a
 * photograph: tokens invert with the theme and a photograph does not, so a
 * pane painted in `--ds-paper` would be a white sheet in one theme and a
 * near-black one in the other, over the same picture.
 *
 * It needs something worth refracting. Below the split, where `Beside`
 * stacks its object onto paper, the pane is hidden: blurring paper produces
 * a slightly grey rectangle, which is a cost with no effect to show for it.
 */
export function Glass({
  children,
  /** Which way the pane is offset from the content it sits behind. */
  from = 'bottom-right',
  /**
   * A second, smaller sheet, offset the other way.
   *
   * Two sheets read as depth where one reads as a backing: the eye gets
   * two different edges at two different distances, and the stack acquires
   * an order. Point it at a different corner from `from`, or it is a
   * thicker version of the first one rather than a second one.
   */
  second,
  className,
}: {
  children: ReactNode
  from?: GlassCorner
  second?: GlassCorner
  className?: string
}) {
  return (
    <div className={cn('ds-glass', `ds-glass--${from}`, second && `ds-glass--2-${second}`, className)}>
      <span className="ds-glass__pane" aria-hidden="true" />
      {second && <span className="ds-glass__pane ds-glass__pane--2" aria-hidden="true" />}
      <div className="ds-glass__on">{children}</div>
    </div>
  )
}
